<template>
  <div class="table-header">
    <div
      v-for="(element) in parsedConfig"
      :key="element.field"
      :style="element.columnWidth"
      :class="getTableHeaderCellClass(element.sortable)"
      draggable
      @dragstart="onDragStart(element.field)"
      @dragenter.prevent
      @dragover.prevent
      @drop="onDrop(element.field)"
    >
      <div
        class="table-header__title"
        @click="() => sortClickHandler(element)"
      >
        {{ element.title }}
        <div
          v-if="element.sortable && sortDirection && orderBy === element.field"
          :class="sortIconClass"
          v-html="arrowIcon"
        />
        <div
          v-else-if="element.sortable"
          class="icon"
          v-html="updown"
        />
      </div>
      <div v-if="element.filterable" class="table-header__filter">
        <input
          @change="(val) => filterChangeHandler(element, val)"
          @input="(val) => filterChangeHandler(element, val)"
        >
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Хедер таблицы
 * @module Shared/Component/Table/TableHeader
 * @requires Images/icon/ic-arrow.svg
 * @requires Images/icon/ic-updown.svg
 * @vue-prop {String} tableId - id таблицы
 * (используется для сохранения настроек колонок пользователя)
 * @vue-prop {Array} config - конфиг таблицы
 * @vue-prop {String} orderBy - по какой колонке происходит сортировка
 * @vue-prop {String} sortDirection - направление сортировки
 * @vue-data {String} startField - используется для перетаскивания колонок
 * (сохраняется имя колонки, которую начали перетаскивать)
 * @vue-computed {Array} sortIconClass - Возвращает иконку,
 * которую нужно отобразить в зависимости от направления сортировки
 * @vue-computed {Array} parsedConfig - подготовленный конфиг
 * */

import arrowIcon from 'Images/icon/ic-arrow.svg'
import updown from 'Images/icon/ic-updown.svg'

export default {
  name: 'Header',
  props: {
    config: {
      type: Array,
      default: () => []
    },
    orderBy: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    arrowIcon,
    updown,
    startField: null
  }),
  computed: {
    sortIconClass() {
      return ['arrow-icon', 'icon', { down: this.sortDirection === 'asc' }, { up: this.sortDirection === 'desc' }]
    },
    parsedConfig() {
      const tempConfig = this.config.map(configElement => ({
        ...configElement,
        title: configElement.emptyHeader ? '' : configElement.title
      }))
      return tempConfig.filter((configElement) => !configElement.hidden)
    }
  },
  methods: {
    /**
     * Обработчик переключения сортировки
     * @param {Object} element
     * @param {String} element.field - имя колонки
     * @param {Boolean} element.sortable - включена ли сортировка на колонке
     */
    sortClickHandler(element) {
      if (element.sortable) this.$emit('update-sort', element.field)
    },
    /**
     * Обработчик фильтров
     * @param {Object} element
     * @param {Boolean} element.filterable - включена ли фильтрация у колонки
     * @param {String} element.field - имя колонки
     * @param {Object} event - событие обновления значения в инпуте фильтра
     */
    filterChangeHandler(element, event) {
      if (element.filterable) this.$emit('update-filters', element.field, event.target.value)
    },
    /**
     * Возвращает классы колонки
     * @return {Array} массив классов
     */
    getTableHeaderCellClass(sortable) {
      return ['table-header__cell', { sortable }]
    },
    /**
     * Обработчик окончания перетаскивания колонки
     * @param {String} toField - имя колонки, на котороую перетащили элемент
     */
    onDrop(toField) {
      if (this.startField !== toField) {
        this.$emit('change-order', {
          from: this.startField,
          to: toField
        })
      }
    },
    /**
     * Обработчик начала перетаскивания колонки
     */
    onDragStart(startField) {
      this.startField = startField
    }
  }
}
</script>

<style lang="scss" scoped>
.table-header {
  display: flex;
  min-height: 41px;

  .table-header__cell {
    padding: 7px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 600;
    background-color: $table-header-background-color;
    box-shadow: 0 0 0 1px $table-border-color;
    margin-right: 1px;

    min-height: 41px;

    cursor: move;

    &:last-child {
      margin-right: 0;
    }

    &.sortable {
      .table-header__title {
        cursor: pointer;
      }
    }
  }

  .table-header__title {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .table-header__filter {
    max-width: 100%;

    input {
      max-width: 100%;
      border-color: $table-filter-input-border-color;
      border-radius: 10px;
      border-width: 1px;
    }
  }
}

.icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  flex-grow: 0;
}

.arrow-icon {
  transition: transform 0.5s;

  &.up {
    transform: rotate(-90deg);
  }

  &.down {
    transform: rotate(90deg);
  }
}
</style>
